import { useState } from 'react';

import {
  Bookcase,
  type BookcaseUnitData,
} from '@/features/library/components/bookcase/Bookcase.js';
import { BookModal } from '@/features/library/components/BookModal.js';
import { useDiscoverShelves } from '@/features/library/hooks/useDiscoverShelves.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';

import { genreMeta } from '../genre-meta.js';

import { genreShelfId } from './GenreChips.js';

/**
 * Étagères de découverte — une par genre Booknest, dans l'ordre renvoyé par
 * le backend (affinité décroissante puis alpha). Chaque étagère a un id DOM
 * stable pour servir de cible aux chips. Deux étagères par ligne sur grand
 * écran via grille CSS.
 */
export function DiscoverGenreShelves(): JSX.Element | null {
  const { data, isLoading } = useDiscoverShelves();
  const { data: shelfData } = useMyShelf();
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [openingBookId, setOpeningBookId] = useState<string | null>(null);

  if (isLoading) {
    return <p className="text-sm text-brown-soft">Préparation de tes étagères…</p>;
  }
  if (!data || data.shelves.length === 0) return null;

  const allBooks = data.shelves.flatMap((s) => s.books);
  const selectedBook = allBooks.find((b) => b.id === selectedBookId) ?? null;
  const selectedEntry =
    shelfData?.items.find((e) => e.book.id === selectedBookId) ?? null;

  const handleBookClick = (id: string): void => {
    if (openingBookId !== null) return;
    setOpeningBookId(id);
    window.setTimeout(() => setSelectedBookId(id), 380);
  };

  const handleClose = (): void => {
    setSelectedBookId(null);
    setOpeningBookId(null);
  };

  return (
    <section className="flex flex-col gap-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold text-brown">
          Tes rayons préférés
        </h2>
        <p className="font-script text-lg text-brown-soft">
          des étagères pioch&eacute;es dans les genres que tu lis le plus
        </p>
      </div>

      <div className="bookcase-grid cols-2">
        {data.shelves.map((shelf) => {
          const meta = genreMeta(shelf.genre);
          const unit: BookcaseUnitData = {
            key: shelf.genre,
            label: meta.label,
            emoji: meta.emoji,
            emptyMessage: 'Pas encore de suggestion pour ce rayon',
            firstAsCover: true,
            books: shelf.books,
          };
          return (
            <div
              key={shelf.genre}
              id={genreShelfId(shelf.genre)}
              style={{ scrollMarginTop: '5rem' }}
            >
              <Bookcase
                units={[unit]}
                openingBookId={openingBookId}
                onBookClick={(b) => handleBookClick(b.id)}
              />
            </div>
          );
        })}
      </div>

      <p className="text-center font-script text-base text-brown-soft">
        👆 touche une tranche pour ouvrir le livre
      </p>

      {selectedBook && (
        <BookModal
          book={selectedBook}
          entry={selectedEntry}
          onClose={handleClose}
        />
      )}
    </section>
  );
}
